import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useSettings, imgSrc } from "@/lib/settings";

export default function Hero() {
  const { settings } = useSettings();
  const t = settings.texts;
  const lines = [t.hero_line1, t.hero_line2, t.hero_line3];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.25]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);

  const go = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={imgSrc(settings.images.hero)} alt="Institut Beauty by Armina" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[var(--brand-dark)]/55" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-16 lg:px-12 lg:pb-20 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-xs uppercase tracking-[0.35em] text-[var(--brand-bg)]/70 mb-6"
          data-testid="hero-overline"
        >
          {t.hero_overline}
        </motion.p>

        <h1 className="font-display text-[var(--brand-bg)] text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight" data-testid="hero-title">
          {lines.map((text, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${i === 1 ? "italic font-light text-[var(--brand-border)]" : ""}`}
              >
                {text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-8 max-w-md text-[var(--brand-bg)]/80 text-base sm:text-lg font-light leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a href="#reservation" onClick={(e) => go(e, "#reservation")}
             className="rounded-full bg-[var(--brand-bg)] text-[var(--brand-dark)] px-9 py-4 text-xs uppercase tracking-[0.25em] transition-transform duration-300 hover:scale-[0.96]"
             data-testid="hero-booking-btn">
            Prendre rendez-vous
          </a>
          <a href="#prestations" onClick={(e) => go(e, "#prestations")}
             className="text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] border-b border-[var(--brand-bg)]/40 pb-1 transition-colors duration-300 hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)]"
             data-testid="hero-services-link">
            Découvrir nos soins
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden lg:flex flex-col items-center gap-3 text-[var(--brand-bg)]/60"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Défiler</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
